import React, { useState, useRef, useEffect, FC, useCallback } from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { Account } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import SearchField from '@adsk/alloy-react-search-field';
import Tabs, { Tab } from '@adsk/alloy-react-tabs';

import {
  TABS_NAMES,
  PROJECT_ENTITLEMENTS_LIMIT,
  SEARCH_PLACEHOLDER_KEYS,
} from './consts';
import ProjectsError from './ProjectsError';
import ResultsLoader from './ResultsLoader';
import VirtualizedList from './VirtualizedList';
import { ANALYTICS_DEBOUNCE, ANALYTICS_TYPES } from '../helpers/analytics';
import { accountsListShape } from '../helpers/propTypes';
import { useDebounce } from '../hooks/useDebounce';
import { useProjectPickerTabs } from '../hooks/useProjectPickerTabs';

export type ProjectPickerProps = {
  accounts?: Account[];
  currentAccountId?: string;
  currentProjectId?: string;
  currentProductId?: string;
  onClose?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
};

const ProjectPicker: FC<ProjectPickerProps> = ({
  accounts,
  currentAccountId,
  currentProjectId,
  currentProductId,
  onClose,
  onAccountClick,
  onProjectClick,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const searchRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAccounts = accounts !== undefined;
  const [currentTab, setCurrentTab] = useState<
    (typeof TABS_NAMES)[keyof typeof TABS_NAMES]
  >(hasAccounts ? TABS_NAMES.ACCOUNTS : TABS_NAMES.PROJECTS);
  const [search, setSearch] = useState<string>('');

  const reportSearchOnChangeAnalyticFn = useCallback(
    () => (debounceSearch: string) => {
      if (debounceSearch && debounceSearch !== '') {
        analytics.track(
          ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_SEARCH_CHANGED,
          {
            product_id: currentProductId,
            searched_text: debounceSearch,
          },
        );
      }
    },
    [currentProductId],
  );

  const debouncedSearch = useDebounce(
    search,
    ANALYTICS_DEBOUNCE,
    reportSearchOnChangeAnalyticFn,
  );

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const tabs = useProjectPickerTabs({
    search: debouncedSearch,
    accounts,
    hasAccounts,
    currentTab,
    onClose,
  });

  useEffect(() => searchRef.current?.focus(), []);

  return (
    <Box width="360px" ref={containerRef}>
      <Box padding="16px">
        <SearchField
          ref={searchRef}
          placeholder={T.translate(
            SEARCH_PLACEHOLDER_KEYS[currentTab],
          )?.toString()}
          value={search || ''}
          onChange={onSearchChange}
        />
      </Box>

      <Tabs
        labelWidth="full"
        initialTab={currentTab}
        onChange={(tab) => setCurrentTab(tab)}
      >
        {tabs.map(
          (tab) =>
            tab && (
              <Tab
                key={tab.tab}
                tab={tab.tab}
                label={tab.label}
                labelProps={{
                  'data-testid': `ProjectPicker_tab_${tab.tab}`,
                }}
              >
                {!tab.error && (
                  <Box
                    typography="heading3"
                    color="charcoal700"
                    padding="8px 16px"
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      {tab.loading ? (
                        <ResultsLoader />
                      ) : (
                        <div data-testid="ProjectPicker_tab_totalResults">
                          {!!tab.totalResults &&
                            `${tab.totalResults} ${tab.label}`}
                        </div>
                      )}

                      <LinkButton
                        as="a"
                        size={LinkButton.SIZES.MEDIUM}
                        href={tab.viewAllLink}
                        onClick={() => {
                          analytics.track(
                            ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_VIEWALL_CLICK,
                          );
                        }}
                      >
                        {tab.viewAllLabel}
                      </LinkButton>
                    </Flex>
                  </Box>
                )}

                {debouncedSearch &&
                  !tab.data?.length &&
                  !tab.loading &&
                  !tab.error && (
                    <Box
                      typography="bodySmall"
                      color="charcoal700"
                      textAlign="center"
                      padding="36px 0"
                    >
                      {T.translate('NO_RESULTS_FOUND')}
                    </Box>
                  )}

                {!debouncedSearch &&
                  !tab.data?.length &&
                  !tab.loading &&
                  !tab.error && (
                    <Box
                      typography="bodySmall"
                      color="charcoal700"
                      textAlign="center"
                      padding="36px 0"
                    >
                      {T.translate('EMPTY_RESULTS', {
                        context: tab.label,
                      })}
                    </Box>
                  )}

                {!tab.data?.length && tab.loading && tab.renderLoader()}

                {!tab.data?.length && tab.error && (
                  <ProjectsError
                    retry={tab.retry}
                    projectsCount={tab.data?.length}
                  />
                )}

                {!!tab.data?.length && (
                  <VirtualizedList
                    size={tab.data.length}
                    fetchNextPage={tab.fetchNextPage}
                    isFetchingNextPage={tab.loading}
                    renderLoader={tab.renderLoader}
                    renderItem={(index) =>
                      tab.renderItem({
                        item: tab.data?.[index],
                        search: debouncedSearch,
                        currentAccountId,
                        currentProjectId,
                        currentProductId,
                        onAccountClick,
                        onProjectClick,
                        containerRef,
                      })
                    }
                    hasNextPage={
                      tab.hasNextPage &&
                      tab.data.length >= PROJECT_ENTITLEMENTS_LIMIT
                    }
                  />
                )}
              </Tab>
            ),
        )}
      </Tabs>
    </Box>
  );
};

ProjectPicker.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func,
};

export default ProjectPicker;
